import { cleanUpMeasureOverviewLocalStorage } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/cleanUpMeasureOverviewLocalStorage";
import { MeasureOverviewColumn } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureOverviewColumn";
import { MeasureOverviewColumnHeader } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureOverviewColumnHeader";
import {
  MeasureOverviewColumnSeverityContext,
  useMeasureOverviewColumnSeverityContextValues,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumnSeverityContext";
import { MeasureOverviewDataContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewDataContext";
import { MeasureOverviewHeader } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewHeader";
import {
  MeasureOverviewStateContext,
  useMeasureOverviewStateContextValues,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewStateContext";
import { PLAN_MEASURE_KEYS } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { FC, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Grid, StaticCard } from "shared/components/Spor";
import { ErrorContent } from "shared/components/states/ErrorContent";
import { LargeLoadingState } from "shared/components/states/LargeLoadingState";
import { useTheme } from "styled-components";

export const MeasureOverview: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const contextValues = useMeasureOverviewStateContextValues();
  const columnSeverityValues = useMeasureOverviewColumnSeverityContextValues();

  const scrollVisibilityRootRef = useRef<HTMLDivElement | null>(null);

  const { plansResponse, arePlansLoading } = useContext(
    MeasureOverviewDataContext,
  );

  useEffect(() => {
    cleanUpMeasureOverviewLocalStorage(plansResponse?.data);
  }, [plansResponse]);

  return (
    <MeasureOverviewStateContext.Provider value={contextValues}>
      <StaticCard
        backgroundColor="semantics.colorBackgroundMain"
        border={`1px solid ${theme.colorOutlineSecondary}`}
        padding={3}
        height="100%"
        borderRadius="xs"
        display="grid"
        gridTemplateRows="auto 1fr"
        gap={3}
      >
        <MeasureOverviewHeader />

        {arePlansLoading && <LargeLoadingState />}

        {!arePlansLoading && !plansResponse && (
          <ErrorContent size="lg" onRetry={() => navigate(0)} />
        )}

        {!arePlansLoading && plansResponse && (
          <Flex containerType="size" overflowX="auto">
            <MeasureOverviewColumnSeverityContext.Provider
              value={columnSeverityValues}
            >
              <Grid
                gridTemplateColumns="repeat(4, 1fr)"
                gridTemplateRows="auto 1fr"
                columnGap={2}
                minWidth="1452px"
                width="100%"
              >
                <Grid gridColumn="1 / -1" templateColumns="subgrid">
                  {PLAN_MEASURE_KEYS.map((measureKey) => (
                    <MeasureOverviewColumnHeader
                      key={measureKey}
                      measureKey={measureKey}
                      trainMeasures={plansResponse?.data?.train[measureKey]}
                      infraMeasures={
                        plansResponse?.data?.infrastructure[measureKey]
                      }
                    />
                  ))}
                </Grid>

                {/** This subgrid layout enables all columns to scroll in sync */}
                <Grid
                  ref={scrollVisibilityRootRef}
                  gridColumn="1 / -1"
                  templateColumns="subgrid"
                  overflowY="scroll"
                  gridTemplateRows="0 [content-start] min-content auto [content-end] 0"
                >
                  {PLAN_MEASURE_KEYS.map((measureKey) => (
                    <MeasureOverviewColumn
                      key={measureKey}
                      measureKey={measureKey}
                      trainMeasures={plansResponse?.data?.train[measureKey]}
                      infraMeasures={
                        plansResponse?.data?.infrastructure[measureKey]
                      }
                      rootRef={scrollVisibilityRootRef}
                    />
                  ))}
                </Grid>
              </Grid>
            </MeasureOverviewColumnSeverityContext.Provider>
          </Flex>
        )}
      </StaticCard>
    </MeasureOverviewStateContext.Provider>
  );
};
